import React, { useEffect, useRef } from 'react';

/* ════════ 像素拼合头像（两段式，Contact/Hero 共享引擎）════════
   wave1：55% 像素散点飞入，拼成做旧马赛克残影（15×15 均值色 + 泛黄）
   wave2：其余 45% 补全飞入 + 已到位像素逐行从做旧色显影为真彩色
   autoDevelop：传毫秒数时，wave1 落定后自动延时触发显影（Hero 用）；
   不传则等外部调 canvas.__develop()（Contact 答题后触发）
   悬停：光标周围高斯波浪 + 正弦涟漪，移开 1.1s 衰减归位（方案九物理） */
const GRID = 150;
const SIZE = 340;
const BLOCK = 10; // 打码粒度：150/10 = 15×15 马赛克
const easeOut = (k) => 1 - Math.pow(1 - k, 3);
const sepia = (r, g, b) => [
  Math.min(255, r * 0.393 + g * 0.769 + b * 0.189),
  Math.min(255, r * 0.349 + g * 0.686 + b * 0.168),
  Math.min(255, r * 0.272 + g * 0.534 + b * 0.131),
];

// 双通道 rAF 兜底（IAB/后台标签页冻结时动画帧仍可达；窗口级幂等）
const useRafShim = () => {
  useEffect(() => {
    const w = window;
    if (w.__rafShimmed) return;
    w.__rafShimmed = true;
    const nativeRaf = w.requestAnimationFrame ? w.requestAnimationFrame.bind(w) : null;
    const nativeCancel = w.cancelAnimationFrame ? w.cancelAnimationFrame.bind(w) : null;
    let seq = 1;
    const pending = new Map();
    w.requestAnimationFrame = (cb) => {
      const myId = seq++;
      let done = false;
      const run = () => {
        if (done) return;
        done = true;
        pending.delete(myId);
        cb(performance.now());
      };
      if (nativeRaf) nativeRaf(run);
      const toId = setTimeout(run, 24); // 节流时兜底通道（≥40fps）
      pending.set(myId, toId);
      return myId;
    };
    w.cancelAnimationFrame = (hid) => {
      const toId = pending.get(hid);
      if (toId !== undefined) { clearTimeout(toId); pending.delete(hid); }
      if (nativeCancel) nativeCancel(hid);
    };
  }, []);
};

// 确定性伪随机（无 Math.random）
const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6D2B79F5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const PixelPortrait = ({ started, developed, autoDevelop }) => {
  const canvasRef = useRef(null);
  useRafShim();

  // 自动显影延时（取最新值）
  const autoRef = useRef(autoDevelop);
  useEffect(() => { autoRef.current = autoDevelop; }, [autoDevelop]);

  // 答对/外部触发 → wave2 补全 + 显影
  useEffect(() => {
    if (developed && canvasRef.current && canvasRef.current.__develop) {
      canvasRef.current.__develop();
    }
  }, [developed]);

  useEffect(() => {
    if (!started) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(SIZE * dpr);
    canvas.height = Math.round(SIZE * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    let raf = 0;
    let alive = true;
    let loaded = false;
    let loadTries = 0;
    let running = false;
    let phase = 'assembling'; // assembling → still ⇄ hover；显影 → developing → still
    let leaveAt = -99999;
    let autoScheduled = false;
    let autoTimer = 0;
    const mouse = { x: -9999, y: -9999, inside: false };
    let parts = [];
    const cell = SIZE / GRID;
    let w1Start = 0;
    let devStart = 0;
    canvas.__dbg = {
      get phase() { return phase; },
      get loaded() { return loaded; },
      get tries() { return loadTries; },
      get count() { return parts.length; },
    };

    const drawFrame = (now) => {
      ctx.clearRect(0, 0, SIZE, SIZE);
      let pending = false;
      const developing = devStart > 0;
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.wave === 2 && !developing) continue; // 补全像素显影前不存在
        const start = p.wave === 1 ? w1Start : Math.max(devStart, w1Start);
        const k = (now - start - p.delay) / p.dur;
        if (k <= 0) { pending = true; continue; }
        const e = k >= 1 ? 1 : easeOut(k);
        if (k < 1) pending = true;
        let x = p.tx - p.dx * (1 - e);
        let y = p.ty - p.dy * (1 - e);
        let s = cell * (0.35 + 0.65 * Math.min(e * 1.4, 1));
        // 悬停波浪：光标周围像素起伏 + 正弦涟漪；移开按时间衰减归位
        const decay = mouse.inside ? 1 : Math.max(0, 1 - (now - leaveAt) / 1100);
        const dxp = x - mouse.x;
        const dyp = y - mouse.y;
        const d = Math.hypot(dxp, dyp) || 0.001;
        const infl = Math.exp(-d / 130) * decay;
        if (infl > 0.004) {
          const push = 18 * infl;
          x += (dxp / d) * push;
          y += (dyp / d) * push;
          y += Math.sin(d * 0.05 - now * 0.008) * 13 * infl;
          y += Math.sin(x * 0.02 + now * 0.003) * 4 * decay;
          s = cell * (1 + 0.45 * infl);
        }
        // 颜色：锁定态做旧马赛克色；显影后逐行过渡为真彩
        let fill;
        if (p.wave === 1 && developing) {
          const ck = (now - devStart - p.cDelay) / 650;
          if (ck >= 1) fill = `rgba(${p.tr},${p.tg},${p.tb},${p.ta})`;
          else {
            if (ck > 0) {
              const e2 = easeOut(ck);
              fill = `rgba(${Math.round(p.lr + (p.tr - p.lr) * e2)},${Math.round(p.lg + (p.tg - p.lg) * e2)},${Math.round(p.lb + (p.tb - p.lb) * e2)},${(p.la + (p.ta - p.la) * e2).toFixed(3)})`;
            } else {
              fill = `rgba(${p.lr},${p.lg},${p.lb},${p.la})`;
            }
            pending = true;
          }
        } else if (p.wave === 1) {
          fill = `rgba(${p.lr},${p.lg},${p.lb},${p.la})`;
        } else {
          fill = `rgba(${p.tr},${p.tg},${p.tb},${p.ta})`;
        }
        ctx.fillStyle = fill;
        ctx.fillRect(x, y, s, s);
      }
      return pending;
    };

    const loop = (now) => {
      if (!alive) return;
      const pending = drawFrame(now);
      const hovering = mouse.inside || performance.now() - leaveAt < 1100;
      if (pending || hovering) {
        raf = requestAnimationFrame(loop);
        return;
      }
      drawFrame(performance.now());
      running = false;
      const wasAssembling = phase === 'assembling';
      phase = 'still';
      // wave1 落定 → 按 autoDevelop 延时自动显影
      if (wasAssembling && !autoScheduled && autoRef.current != null) {
        autoScheduled = true;
        autoTimer = setTimeout(() => {
          if (alive) canvas.__develop();
        }, autoRef.current);
      }
    };
    const startLoop = () => {
      if (!alive || running) return;
      running = true;
      phase = devStart ? 'developing' : phase === 'assembling' ? 'assembling' : 'hover';
      raf = requestAnimationFrame(loop);
    };

    // 加载头像：onerror 或渲染管线冻结吞掉 load 事件时自动重试自愈
    const startLoad = () => {
      if (!alive || loaded) return;
      loadTries += 1;
      const img = new Image();
      img.src = '/images/avatar.png';
      img.onerror = () => { if (alive) setTimeout(() => startLoad(), 800); };
      setTimeout(() => { if (alive && !loaded) startLoad(); }, 3000);
      img.onload = () => {
        if (!alive || loaded) return;
        loaded = true;
        // 采样 150×150 网格 + 15×15 块均值（打码用）
        const off = document.createElement('canvas');
        off.width = GRID;
        off.height = GRID;
        off.getContext('2d').drawImage(img, 0, 0, GRID, GRID);
        const data = off.getContext('2d').getImageData(0, 0, GRID, GRID).data;
        const nb = GRID / BLOCK;
        const acc = [];
        for (let i = 0; i < nb * nb; i++) acc.push([0, 0, 0, 0]);
        const rnd = mulberry32(20260924);
        for (let gy = 0; gy < GRID; gy++) {
          for (let gx = 0; gx < GRID; gx++) {
            const o = (gy * GRID + gx) * 4;
            if (data[o + 3] < 28) continue; // 剔除透明背景
            const bi = Math.floor(gy / BLOCK) * nb + Math.floor(gx / BLOCK);
            const a = acc[bi];
            a[0] += data[o]; a[1] += data[o + 1]; a[2] += data[o + 2]; a[3] += 1;
          }
        }
        for (let gy = 0; gy < GRID; gy++) {
          for (let gx = 0; gx < GRID; gx++) {
            const o = (gy * GRID + gx) * 4;
            if (data[o + 3] < 28) continue;
            const bi = Math.floor(gy / BLOCK) * nb + Math.floor(gx / BLOCK);
            const n = Math.max(acc[bi][3], 1);
            const sp = sepia(acc[bi][0] / n, acc[bi][1] / n, acc[bi][2] / n);
            const tx = gx * cell;
            const ty = gy * cell;
            const ang = rnd() * Math.PI * 2;
            const dist = 40 + rnd() * 240;
            const isMember = rnd() < 0.55; // 锁定态只拼 55%
            const rowK = ty / SIZE;
            parts.push({
              tx,
              ty,
              dx: Math.cos(ang) * dist,
              dy: Math.sin(ang) * dist,
              wave: isMember ? 1 : 2,
              delay: rowK * 1200 + rnd() * 420, // 自上而下逐行拼合
              dur: 520 + rnd() * 260,
              cDelay: rowK * 700 + rnd() * 250, // 显影色波
              lr: sp[0], lg: sp[1], lb: sp[2], la: 1,
              tr: data[o], tg: data[o + 1], tb: data[o + 2], ta: +(data[o + 3] / 255).toFixed(2),
            });
          }
        }
        w1Start = performance.now();
        startLoop();
        if (devStart) startLoop(); // 极端：外部触发早于图片加载完成
      };
    };
    startLoad();

    canvas.__develop = () => {
      if (!alive) return;
      if (!devStart) devStart = performance.now();
      if (loaded) startLoop();
    };

    // 悬停交互
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.inside = true;
      startLoop();
    };
    const onEnter = () => {
      mouse.inside = true;
      startLoop();
    };
    const onLeave = () => {
      mouse.inside = false;
      leaveAt = performance.now();
      startLoop();
    };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerenter', onEnter);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.__cleanupHover = () => {
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerenter', onEnter);
      canvas.removeEventListener('pointerleave', onLeave);
    };

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      clearTimeout(autoTimer);
      if (canvas.__cleanupHover) canvas.__cleanupHover();
    };
  }, [started]);

  return <canvas ref={canvasRef} className="px-portrait" aria-label="像素拼合头像" />;
};

export default PixelPortrait;
